import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, Clock, Eye, Loader2, MessageSquareWarning, Moon, Play } from "lucide-react";
import { useExtensionQueue, useLifecycle } from "../../lib/adminQueues";
import { formatDollars } from "../../lib/draftCampaign";
import { BulkConfirmDialog } from "./BulkConfirmDialog";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "never");

/** Nightly job card — read-only status plus a "run now" for QA / catch-up. */
const LifecycleCard = () => {
  const { status, running, runNow } = useLifecycle();
  const last = status?.last_run;
  const onRun = async () => {
    try {
      const s = await runNow();
      toast.success(`Lifecycle pass done — ${s.closed.length} closed, ${s.ending_soon_nudged.length} nudged${s.held_for_extension.length ? `, ${s.held_for_extension.length} held for extension review` : ""}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't run the lifecycle job.");
    }
  };
  return (
    <div data-testid="lifecycle-card" className="flex flex-col gap-4 rounded-lg border border-border bg-bone p-5 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-sand"><Moon className="h-4 w-4" strokeWidth={2} /></span>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Nightly lifecycle · automatic</p>
          <p className="mt-1 text-sm text-ink">
            Every night at 12:15 AM Pacific: campaigns past their end date close and the student gets a summary; campaigns with {status?.ending_soon_days ?? 7} days left get an "ending soon" nudge. Campaigns with a pending extension request are held for you.
          </p>
          <p className="mt-2 text-xs text-olive" data-testid="lifecycle-last-run">
            Last run: <span className="font-semibold text-ink">{fmtWhen(last?.last_run_at)}</span>
            {last && <> · {last.closed?.length || 0} closed · {last.ending_soon_nudged?.length || 0} nudged · {last.held_for_extension?.length || 0} held{last.trigger?.startsWith("manual") ? " · manual" : ""}</>}
          </p>
        </div>
      </div>
      <button type="button" onClick={onRun} disabled={running} data-testid="lifecycle-run-now" className="inline-flex shrink-0 items-center gap-2 rounded-full border border-ink px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-ink hover:text-sand disabled:opacity-50">
        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" strokeWidth={2} />} Run now
      </button>
    </div>
  );
};

const ExtensionRow = ({ x, onApprove, onDecline }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6 }}
    transition={{ duration: 0.25 }}
    data-testid={`extension-row-${x.campaign_id}`}
    className="rounded-xl border-2 border-ink/10 bg-bone p-5"
  >
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="font-serif text-xl text-ink">{x.title || <em className="italic text-olive">Untitled campaign</em>}</h3>
      <span className="text-[10px] uppercase tracking-[0.22em] text-olive">Requested {fmtDate(x.requested_at)}</span>
    </div>
    <p className="mt-1 text-sm text-olive">
      <span className="font-semibold text-ink">{x.student_name || x.student_email}</span>{x.school && <> · {x.school}</>} · {formatDollars(x.raised_cents)} of {formatDollars(x.goal_cents)} · {x.donor_count} supporter{x.donor_count === 1 ? "" : "s"}
    </p>
    <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-wide">
      <span className="inline-flex items-center gap-1 rounded-full bg-yellow/30 px-2.5 py-1 text-ink" data-testid={`extension-days-left-${x.campaign_id}`}><Clock className="h-3 w-3" /> {x.days_left} day{x.days_left === 1 ? "" : "s"} left · ends {fmtDate(x.ends_at)}</span>
      <span className="inline-flex items-center gap-1 rounded-full bg-cobalt/12 px-2.5 py-1 text-cobalt"><CalendarClock className="h-3 w-3" /> +{x.extension_days} days if approved</span>
    </div>
    <blockquote className="mt-4 rounded-md border-l-4 border-tangerine bg-sand px-4 py-3 font-serif text-lg leading-snug text-ink" data-testid={`extension-reason-${x.campaign_id}`}>
      &ldquo;{x.reason}&rdquo;
    </blockquote>
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <button type="button" onClick={onApprove} data-testid={`extension-approve-${x.campaign_id}`} className="inline-flex items-center gap-1 rounded-full bg-ink px-4 py-1.5 text-xs font-semibold text-sand transition-colors hover:bg-forest">
        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} /> Approve +{x.extension_days} days
      </button>
      <Link to={`/campaign/${x.campaign_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-border bg-bone px-4 py-1.5 text-xs font-semibold text-ink transition-colors hover:border-ink hover:bg-sand">
        <Eye className="h-3.5 w-3.5" strokeWidth={2} /> View page
      </Link>
      <button type="button" onClick={onDecline} data-testid={`extension-decline-${x.campaign_id}`} className="inline-flex items-center gap-1 rounded-full border border-scarlet/40 bg-sand px-4 py-1.5 text-xs font-semibold text-scarlet transition-colors hover:bg-scarlet/8">
        <MessageSquareWarning className="h-3.5 w-3.5" strokeWidth={2} /> Decline with a note
      </button>
    </div>
  </motion.article>
);

export const ExtensionsTab = () => {
  const { items, loading, error, refresh, approve, reject } = useExtensionQueue();
  const [declining, setDeclining] = useState(null);

  const onApprove = async (x) => {
    try {
      const res = await approve(x.campaign_id);
      toast.success(`Extended by ${res.days_added} days — now ends ${fmtDate(res.ends_at)}. Student notified.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve.");
    }
  };
  const onDecline = async (reason) => {
    if (!declining) return;
    try {
      await reject(declining.campaign_id, reason);
      toast.success("Declined with your note — student notified.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't decline.");
    }
    setDeclining(null);
    await refresh();
  };

  return (
    <div className="space-y-8" data-testid="extensions-tab">
      <LifecycleCard />
      <div>
        <h2 className="font-serif text-3xl text-ink">Extension requests</h2>
        <p className="mt-1 text-sm text-olive">Students get one 15-day extension per campaign, and only with your sign-off. A campaign with a pending request is held open until you decide.</p>
        {loading ? (
          <p className="mt-6 flex items-center gap-2 text-sm text-olive"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        ) : error ? (
          <p className="mt-6 text-sm text-scarlet">{error}</p>
        ) : items.length === 0 ? (
          <div className="mt-6 rounded-xl border-2 border-dashed border-ink/10 bg-bone p-10 text-center" data-testid="extensions-empty">
            <CalendarClock className="mx-auto h-8 w-8 text-olive" strokeWidth={1.5} />
            <p className="mt-3 font-serif text-2xl text-ink">No extension requests waiting.</p>
            <p className="mt-1 text-sm text-olive">Requests appear here the moment a student asks for more time.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-4" data-testid="extensions-list">
            <AnimatePresence>
              {items.map((x) => <ExtensionRow key={x.campaign_id} x={x} onApprove={() => onApprove(x)} onDecline={() => setDeclining(x)} />)}
            </AnimatePresence>
          </div>
        )}
      </div>
      <BulkConfirmDialog
        open={!!declining}
        onOpenChange={(o) => !o && setDeclining(null)}
        title="Decline this extension?"
        description="The campaign will close on its current end date. Your note is emailed to the student — keep it warm and specific."
        itemsSummary={declining ? [`${declining.student_name || declining.student_email} · ${declining.title}`] : []}
        confirmLabel="Decline & notify"
        confirmTone="danger"
        requireNote
        notePlaceholder="e.g. Your campaign is already at 92% with a week to go — let's finish strong on the current date."
        onConfirm={onDecline}
      />
    </div>
  );
};
